import { useEffect, useRef, useState } from 'preact/hooks';
import { supabase } from '../lib/supabase';
import { RESEND_WAIT_SECONDS, codeProblem, looksLikeCode, sendProblem, tidyCode, tidyEmail } from './words';

// Sign-in (Phase 5a): invite-only. Ask for an email; Supabase sends a sign-in link, which
// signs you in where it opens. Never creates an account.
// A code in the email (for a home-screen app on an iPhone, where links open in Safari) needs
// the project's own mail service (custom SMTP; Supabase locks the templates without it), so
// for now the code box is folded behind "Have a code?" (Kristi, Oct 26, option A).

type Step = 'email' | 'sent';

export function SignIn() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [wait, setWait] = useState(0);
  const [codeOpen, setCodeOpen] = useState(false);
  const codeBox = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  useEffect(() => {
    if (codeOpen) codeBox.current?.focus();
  }, [codeOpen]);

  if (!supabase) {
    return <p class="auth-note">This copy of the site isn’t connected to a database yet.</p>;
  }
  const client = supabase;

  async function send(e?: Event) {
    e?.preventDefault();
    setBusy(true);
    setProblem('');
    const { error } = await client.auth.signInWithOtp({
      email: tidyEmail(email),
      options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (error) {
      setProblem(sendProblem(error));
      return;
    }
    setStep('sent');
    setCode('');
    setCodeOpen(false);
    setWait(RESEND_WAIT_SECONDS);
  }

  async function check(e: Event) {
    e.preventDefault();
    const c = tidyCode(code);
    if (!looksLikeCode(c)) {
      setProblem('The code is the number in the email, 6 digits or more.');
      return;
    }
    setBusy(true);
    setProblem('');
    const { error } = await client.auth.verifyOtp({ email: tidyEmail(email), token: c, type: 'email' });
    setBusy(false);
    if (error) setProblem(codeProblem(error));
    // On success the session changes and the app shows itself.
  }

  if (step === 'sent') {
    const again = (
      <div class="auth-row">
        <button type="button" class="btn-link" disabled={wait > 0 || busy} onClick={() => send()}>
          {wait > 0 ? `Send again in ${wait} s` : 'Send again'}
        </button>
        <button
          type="button"
          class="btn-link"
          onClick={() => {
            setStep('email');
            setProblem('');
          }}
        >
          Use another address
        </button>
      </div>
    );
    return (
      <div class="auth-form">
        <p class="auth-note">
          We sent a sign-in link to <b>{tidyEmail(email)}</b>. Open the email and tap the link; it signs you in
          where it opens.
        </p>
        {codeOpen ? (
          <form class="auth-form" onSubmit={check} aria-label="Type the code">
            <label for="code">Code from the email</label>
            <input
              id="code"
              ref={codeBox}
              inputMode="numeric"
              autocomplete="one-time-code"
              pattern="[0-9 \-]*"
              maxLength={14}
              value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            />
            <button class="btn-main" type="submit" disabled={busy}>
              {busy ? 'Checking…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <button type="button" class="btn-link auth-code-open" onClick={() => setCodeOpen(true)}>
            Have a code?
          </button>
        )}
        {problem ? <p class="auth-problem" role="alert">{problem}</p> : null}
        {again}
      </div>
    );
  }

  return (
    <form class="auth-form" onSubmit={send} aria-label="Sign in">
      <label for="email">Email</label>
      <input
        id="email"
        type="email"
        autocomplete="email"
        required
        value={email}
        onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
      />
      <button class="btn-main" type="submit" disabled={busy}>
        {busy ? 'Sending…' : 'Send sign-in link'}
      </button>
      {problem ? <p class="auth-problem" role="alert">{problem}</p> : null}
      <p class="auth-fine">Invitation only.</p>
    </form>
  );
}
